import { Box, Button, Stack, Typography } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import SrsReportService from '../../../../shared/services/srs-report/srs-report.service'
import {
  ISrsFigures,
  ISrsReport,
  ISrsStructure,
  SrsSectionKey,
} from '../../../../shared/services/srs-report/srs-report.model'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../../loading-page'
import { ImportStepHeader } from '../../settings/data-import/import-data/steps/import-step-header'
import { SrsStepSetup } from '../../../presentation-components/reporting-components/srs/srs-step-setup'
import { SrsStepNarratives } from '../../../presentation-components/reporting-components/srs/srs-step-narratives'
import { SrsStepMetrics } from '../../../presentation-components/reporting-components/srs/srs-step-metrics'
import { SrsStepGenerate } from '../../../presentation-components/reporting-components/srs/srs-step-generate'

/** Wizard step order: setup, the three narrative pillars, metrics review, then sign-off. */
const STEP_SECTIONS: (SrsSectionKey | null)[] = [null, 'GOVERNANCE', 'STRATEGY', 'RISK_MANAGEMENT', 'METRICS_TARGETS', null]
const TOTAL_STEPS = STEP_SECTIONS.length
const METRICS_STEP = 4

/** Intro copy per narrative pillar; the headings and guidance themselves come from the backend. */
const SECTION_INTROS: Partial<Record<SrsSectionKey, string>> = {
  GOVERNANCE: 'Describe how climate-related risks and opportunities are governed. These are your own statements: the prompts below explain what the standard asks for, and are never published.',
  STRATEGY: 'Describe how climate change affects your strategy, business model and finances. The prompts below explain what the standard asks for, and are never published.',
  RISK_MANAGEMENT: 'Describe how climate-related risks are identified, assessed and managed. The prompts below explain what the standard asks for, and are never published.',
}

/** Sign-off fields the wizard edits locally alongside the structure-driven narrative fields. */
const SIGN_OFF_FIELDS = ['signOffName', 'signOffTitle', 'signOffDate']

/**
 * The UK SRS S2 wizard. Serves both /new (before a report row exists) and /:id/edit. Viewing a
 * generated report is the detail page's job (ReportingSrsDetailPage); this page only edits and
 * generates.
 *
 * The narrative steps are driven entirely by GET srs-report/structure: headings, guidance and
 * paragraph references are the backend's, so the standard's shape is declared in exactly one place.
 *
 * Step 1 creates the report on Next, because everything after it needs a saved report to compute
 * figures against. Later steps save their edits on Next so a half-finished report survives a
 * refresh.
 */
export const ReportingSrsWizardPage = () => {
  const srsReportService = useMemo(() => new SrsReportService(), [])
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)

  const [structure, setStructure] = useState<ISrsStructure | null>(null)
  const [report, setReport] = useState<ISrsReport | null>(null)
  const [figures, setFigures] = useState<ISrsFigures | null>(null)
  const [draft, setDraft] = useState<Record<string, any>>({})
  const [activeStep, setActiveStep] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Step 1 selection, held locally until the report row exists.
  const [reportPeriodId, setReportPeriodId] = useState('')

  const reportPeriods = useMemo(
    () => [...(reportPeriodContext.allReportPeriods || [])]
      .sort((a, b) => Number(b.calendarYear) - Number(a.calendarYear)),
    [reportPeriodContext.allReportPeriods],
  )

  const showError = useCallback((message: string) => {
    userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
  }, [userNotificationsContext])

  const applyReport = useCallback((response: ISrsReport, narrativeFieldNames: string[]) => {
    setReport(response)
    setReportPeriodId(response.reportPeriodId)
    const next: Record<string, any> = {}
    for (const field of [...narrativeFieldNames, ...SIGN_OFF_FIELDS]) {
      next[field] = (response as any)[field] ?? ''
    }
    setDraft(next)
  }, [])

  // Default the period to the newest reporting period, matching how most disclosures are drafted.
  useEffect(() => {
    if (id || reportPeriodId || !reportPeriods.length) return
    setReportPeriodId(reportPeriods[0]?.id || '')
  }, [id, reportPeriods, reportPeriodId])

  // The structure drives every narrative step, so the wizard waits for it alongside the report.
  useEffect(() => {
    const loadReport = id ? srsReportService.getOne(id) : Promise.resolve(null)
    Promise.all([srsReportService.getStructure(), loadReport])
      .then(([structureResponse, reportResponse]) => {
        setStructure(structureResponse)
        if (reportResponse) {
          applyReport(reportResponse, structureResponse.narrativeFields.map((field) => field.field))
          const requestedStep = Number(searchParams.get('step'))
          setActiveStep(Number.isFinite(requestedStep) && requestedStep > 0
            ? Math.min(requestedStep, TOTAL_STEPS - 1)
            : 0)
        }
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        showError('Could not load this climate disclosure')
      })
    // Re-reads only when the report id changes; step is applied from the URL on that load.
  }, [id])

  // The steps are long enough to scroll, so moving between them would otherwise drop the user
  // part-way down the next step with the heading off screen.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const loadFigures = useCallback(async (reportId: string) => {
    try {
      setFigures(await srsReportService.getFigures(reportId))
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not calculate your emissions figures')
    }
  }, [srsReportService, showError])

  // Figures are needed from the metrics step onward, and are recomputed when the period changes or
  // the report is generated (which switches the response from live figures to the frozen snapshot).
  const needsFigures = activeStep >= METRICS_STEP
  useEffect(() => {
    if (!report || !needsFigures) return
    loadFigures(report.id)
  }, [report?.id, report?.reportPeriodId, report?.status, report?.generatedAt, needsFigures])

  const narrativeFieldNames = useMemo(
    () => (structure?.narrativeFields || []).map((field) => field.field),
    [structure],
  )

  const handleDraftChange = (field: string, value: any) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
  }

  const saveStep1 = async (): Promise<ISrsReport | null> => {
    if (!report) return srsReportService.create(reportPeriodId)
    // The period is fixed once generated; the backend rejects a change, so don't send one.
    if (report.status === 'GENERATED' || report.reportPeriodId === reportPeriodId) return report
    return srsReportService.update(report.id, { reportPeriodId })
  }

  /** Persists whatever the current step edits. The metrics figures themselves are read-only. */
  const saveCurrentStep = async (): Promise<ISrsReport | null> => {
    if (activeStep === 0) {
      const saved = await saveStep1()
      if (saved) applyReport(saved, narrativeFieldNames)
      return saved
    }
    if (report) {
      const saved = await srsReportService.update(report.id, draft as Partial<ISrsReport>)
      applyReport(saved, narrativeFieldNames)
      return saved
    }
    return report
  }

  const handleNext = async () => {
    setIsSaving(true)
    try {
      const saved = await saveCurrentStep()
      // A new report gets its own URL so a refresh (or the browser back button) returns to the
      // draft rather than starting over.
      if (activeStep === 0 && !id && saved) {
        navigate(`/reporting/uk-srs/${saved.id}/edit?step=1`, { replace: true })
      }
      const nextStep = Math.min(activeStep + 1, TOTAL_STEPS - 1)
      setActiveStep(nextStep)
      if (id) setSearchParams({ step: String(nextStep) }, { replace: true })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'There was an error, please try again')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAndClose = async () => {
    setIsSaving(true)
    try {
      await saveCurrentStep()
      userNotificationsContext.openSnackbar({ open: true, message: 'Draft saved. Pick it up any time from the reports list.', backgroundColor: 'text.primary' })
      navigate('/reporting/uk-srs')
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not save your draft, please try again')
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    const previousStep = Math.max(activeStep - 1, 0)
    setActiveStep(previousStep)
    if (id) setSearchParams({ step: String(previousStep) }, { replace: true })
  }

  const handleGenerate = async () => {
    if (!report) return
    setIsGenerating(true)
    try {
      // The sign-off entered on this step must be in the frozen document, so save before generating.
      await srsReportService.update(report.id, draft as Partial<ISrsReport>)
      const response = await srsReportService.generate(report.id)
      // applyReport changes status/generatedAt, which re-runs the figures effect so the wizard
      // switches from live figures to the frozen snapshot. Do NOT clear figures here: nothing would
      // refetch them if the report was regenerated unchanged, leaving the metrics step blank.
      applyReport(response.srsReport, narrativeFieldNames)
      // Each artefact is tracked separately so the user sees both complete.
      for (const fileDownload of response.fileDownloads) {
        downloadStatusContext.startDownloadTracking(fileDownload.id)
      }
      userNotificationsContext.openSnackbar({ open: true, message: 'Generating your climate disclosure', backgroundColor: 'text.primary' })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not generate your report, please try again')
    } finally {
      setIsGenerating(false)
    }
  }

  const renderStepContent = (step: number) => {
    if (!structure) return null
    if (step === 0) {
      return (
        <SrsStepSetup
          reportPeriods={reportPeriods}
          reportPeriodId={reportPeriodId}
          locked={report?.status === 'GENERATED'}
          onChange={setReportPeriodId}
        />
      )
    }

    const section = STEP_SECTIONS[step]
    if (section && section !== 'METRICS_TARGETS') {
      return (
        <SrsStepNarratives
          sectionLabel={structure.sections[section]}
          intro={SECTION_INTROS[section] || ''}
          fields={structure.narrativeFields.filter((field) => field.section === section)}
          values={draft}
          onChange={handleDraftChange}
        />
      )
    }

    if (!report || !figures) {
      return <LoadingPage pageReady={false}><Box /></LoadingPage>
    }
    if (section === 'METRICS_TARGETS') {
      return (
        <SrsStepMetrics
          sectionLabel={structure.sections.METRICS_TARGETS}
          figures={figures}
          targetsField={structure.narrativeFields.find((field) => field.section === 'METRICS_TARGETS') || null}
          values={draft}
          onChange={handleDraftChange}
        />
      )
    }
    return (
      <SrsStepGenerate
        report={report}
        figures={figures}
        values={draft}
        isGenerating={isGenerating}
        onChange={handleDraftChange}
        onGenerate={handleGenerate}
        onViewReport={() => navigate(`/reporting/uk-srs/${report.id}`)}
      />
    )
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title='Climate Disclosure (UK SRS)'
        includeBackButton={true}
        backButtonLink={report?.status === 'GENERATED'
          ? `/reporting/uk-srs/${report.id}`
          : '/reporting/uk-srs'}
        backButtonText={report?.status === 'GENERATED' ? 'Back to report' : 'Back to reports'}
        hasTabs={false}
      />
      <LoadingPage pageReady={!isLoading}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Box sx={{ width: '100%', maxWidth: 880 }}>
            <ImportStepHeader currentStep={activeStep + 1} totalSteps={TOTAL_STEPS} />
            <Box sx={{ mb: 4 }}>
              {renderStepContent(activeStep)}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant='outlined'
                onClick={handleBack}
                startIcon={<ChevronLeft />}
                disabled={activeStep === 0}
                sx={{ minWidth: 100, textTransform: 'none' }}
              >
                <Typography>Back</Typography>
              </Button>
              <Stack direction='row' spacing={1}>
                {/* A generated report is no longer a draft, so it only offers Next (edits still save
                    on Next as before). */}
                {report?.status !== 'GENERATED' && activeStep < TOTAL_STEPS - 1 && (
                  <Button
                    variant='outlined'
                    onClick={handleSaveAndClose}
                    disabled={isSaving || (activeStep === 0 && !reportPeriodId)}
                    sx={{ textTransform: 'none' }}
                  >
                    <Typography>Save draft and close</Typography>
                  </Button>
                )}
                {activeStep < TOTAL_STEPS - 1 && (
                  <Button
                    variant='contained'
                    onClick={handleNext}
                    disabled={isSaving
                      || (activeStep === 0 && !reportPeriodId)
                      || (activeStep >= METRICS_STEP && !figures)}
                    endIcon={<ChevronRight />}
                    sx={{ minWidth: 100, textTransform: 'none' }}
                  >
                    <Typography>{isSaving ? 'Saving…' : 'Next'}</Typography>
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </LoadingPage>
    </PageOuterContainer>
  )
}
